import { styled } from "goober";
import { useEffect, useId, useRef, useState } from "react";

const loadMermaid = () => import("mermaid").then((m) => m.default);

function readColorScheme(): "light" | "dark" {
  const scheme = document.documentElement.style.colorScheme;
  if (scheme === "light" || scheme === "dark") return scheme;
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

// Reads the mode useColorMode writes, without mounting that hook — calling
// useColorMode() from a child would reset the global scheme. The MutationObserver
// catches the manual ⇧⌥D toggle, which only mutates inline colorScheme and so
// never fires matchMedia.
function useColorScheme(): "light" | "dark" {
  const [scheme, setScheme] = useState(readColorScheme);
  useEffect(() => {
    const update = () => setScheme(readColorScheme());
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    media.addEventListener("change", update);
    const observer = new MutationObserver(update);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["style"],
    });
    return () => {
      media.removeEventListener("change", update);
      observer.disconnect();
    };
  }, []);
  return scheme;
}

// mermaid bakes colors into the SVG and can't read our CSS variables, so resolve
// each token to a concrete color for the scheme. Rasterize to sRGB hex: on
// wide-gamut displays getComputedStyle returns `color(display-p3 …)`, which
// mermaid's theme color math can't parse and throws on.
function tokenHex(name: string): string {
  const probe = document.createElement("div");
  probe.style.color = `var(${name})`;
  probe.style.display = "none";
  document.body.appendChild(probe);
  const color = getComputedStyle(probe).color;
  probe.remove();
  const ctx = document.createElement("canvas").getContext("2d");
  if (!ctx) return color;
  ctx.fillStyle = color;
  ctx.fillRect(0, 0, 1, 1);
  const [r = 0, g = 0, b = 0] = ctx.getImageData(0, 0, 1, 1).data;
  return `#${[r, g, b].map((v) => v.toString(16).padStart(2, "0")).join("")}`;
}

// A monochrome palette built from the app's neutral tokens, so diagrams match the
// UI in both modes instead of mermaid's default lavender.
function themeVariables(scheme: "light" | "dark") {
  const textPrimary = tokenHex("--text-primary");
  const textSecondary = tokenHex("--text-secondary");
  const surfacePrimary = tokenHex("--surface-primary");
  const surfaceSecondary = tokenHex("--surface-secondary");
  return {
    darkMode: scheme === "dark",
    fontFamily: getComputedStyle(document.body).fontFamily,
    background: "transparent",
    primaryColor: surfaceSecondary,
    mainBkg: surfaceSecondary,
    secondaryColor: surfaceSecondary,
    tertiaryColor: surfacePrimary,
    primaryBorderColor: textSecondary,
    nodeBorder: textSecondary,
    clusterBkg: surfacePrimary,
    clusterBorder: textSecondary,
    primaryTextColor: textPrimary,
    textColor: textSecondary,
    titleColor: textPrimary,
    lineColor: textSecondary,
    defaultLinkColor: textSecondary,
    edgeLabelBackground: surfaceSecondary,
    nodeTextColor: textPrimary,
    actorBkg: surfaceSecondary,
    actorBorder: textSecondary,
    actorTextColor: textPrimary,
    actorLineColor: textSecondary,
    signalColor: textSecondary,
    signalTextColor: textSecondary,
    labelBoxBkgColor: surfaceSecondary,
    labelBoxBorderColor: textSecondary,
    labelTextColor: textPrimary,
    loopTextColor: textPrimary,
    noteBkgColor: surfaceSecondary,
    noteTextColor: textPrimary,
    noteBorderColor: textSecondary,
  };
}

// Diagrams can carry accent colors (classDef/style hex, or mermaid's own scales).
// Map each saturated color to the nearest brand ramp by hue and pick a shade by
// lightness, so it uses our tokens and inherits their light/dark flavors; neutral
// grays fall through untouched to the monochrome theme above.
const ACCENT_FAMILIES: [string, number][] = [
  ["red", 2],
  ["orange", 23],
  ["yellow", 39],
  ["green", 161],
  ["teal", 178],
  ["sky", 194],
  ["purple", 254],
  ["pink", 328],
];

// Gate on chroma (max-min), not HSL saturation: the brand near-black text token
// (#0d0d17) has a cool tint that reads as high saturation at low lightness, yet is
// perceptually neutral — chroma keeps it (and grays/near-white) out of the remap.
function hexToHcl(hex: string): { h: number; c: number; l: number } {
  const raw = hex.slice(1);
  const full = raw.length === 3 ? raw.replace(/./g, (ch) => ch + ch) : raw;
  const r = parseInt(full.slice(0, 2), 16) / 255;
  const g = parseInt(full.slice(2, 4), 16) / 255;
  const b = parseInt(full.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const c = max - min;
  const l = (max + min) / 2;
  let h = 0;
  if (c) {
    if (max === r) h = ((g - b) / c) % 6;
    else if (max === g) h = (b - r) / c + 2;
    else h = (r - g) / c + 4;
    h = (h * 60 + 360) % 360;
  }
  return { h, c, l };
}

const COLOR_RE = /#[0-9a-fA-F]{6}\b|rgba?\([\d,\s.]+\)/g;

// Normalize a `#rrggbb` or `rgb(…)` value to 6-digit hex, or null if neither.
function toHex(color: string): string | null {
  if (color[0] === "#") return color.length === 7 ? color : null;
  const m = color.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
  return m
    ? `#${m
        .slice(1, 4)
        .map((x) => Number(x).toString(16).padStart(2, "0"))
        .join("")}`
    : null;
}

// Nearest brand family by hue, or null for neutral (low-chroma/near-white) colors.
function familyOf(hex: string): string | null {
  const { h, c, l } = hexToHcl(hex);
  if (c < 0.1 || l > 0.96) return null;
  let family = "red";
  let best = 360;
  for (const [name, hue] of ACCENT_FAMILIES) {
    const dist = Math.min(Math.abs(h - hue), 360 - Math.abs(h - hue));
    if (dist < best) {
      best = dist;
      family = name;
    }
  }
  return family;
}

function shadeOf(hex: string): number {
  const { l } = hexToHcl(hex);
  return l >= 0.78 ? 200 : l >= 0.6 ? 400 : 600;
}

// A brand token for an accent color, or null (leave it) for neutral colors so the
// monochrome text/surface tokens survive. `family` forces a specific ramp.
function tokenFor(hex: string, family?: string): string | null {
  const fam = familyOf(hex);
  if (!fam) return null;
  return `var(--${family ?? fam}-${shadeOf(hex)})`;
}

// Unify each classDef group to one family (its most chromatic color) so a class's
// fill/stroke/text agree — a blue whose dark text sits on the sky/purple hue
// boundary shouldn't split into a purple label. The visible text obeys the tspan
// <style> rule, so unifying <style> suffices; inline shape colors map per-color.
// mermaid writes classDef colors as `rgb(…)` in <style> and `#hex` inline.
function remapAccents(svg: Element): void {
  const style = svg.querySelector("style");
  if (style) {
    const css = style.textContent ?? "";
    const familyByClass = new Map<string, string>();
    const chromaByClass = new Map<string, number>();
    for (const rule of css.matchAll(/([^{}]+)\{([^}]*)\}/g)) {
      const selector = rule[1] ?? "";
      const classes = selector.match(/\.[-\w]+/g);
      const cls = classes?.[classes.length - 1];
      if (!cls) continue;
      for (const colorMatch of (rule[2] ?? "").matchAll(COLOR_RE)) {
        const hex = toHex(colorMatch[0] ?? "");
        const fam = hex ? familyOf(hex) : null;
        if (!hex || !fam) continue;
        const { c } = hexToHcl(hex);
        if (c > (chromaByClass.get(cls) ?? -1)) {
          chromaByClass.set(cls, c);
          familyByClass.set(cls, fam);
        }
      }
    }
    style.textContent = css.replace(
      /([^{}]+)\{([^}]*)\}/g,
      (_rule, selector: string, decls: string) => {
        const classes = selector.match(/\.[-\w]+/g);
        const cls = classes?.[classes.length - 1];
        const family = cls ? familyByClass.get(cls) : undefined;
        const next = decls.replace(COLOR_RE, (color) => {
          const hex = toHex(color);
          return hex ? (tokenFor(hex, family) ?? color) : color;
        });
        return `${selector}{${next}}`;
      }
    );
  }
  const repl = (t: string) =>
    t.replace(COLOR_RE, (color) => {
      const hex = toHex(color);
      return hex ? (tokenFor(hex) ?? color) : color;
    });
  for (const el of svg.querySelectorAll("[fill],[stroke],[style]"))
    for (const attr of ["fill", "stroke", "style"]) {
      const v = el.getAttribute(attr);
      if (v) el.setAttribute(attr, repl(v));
    }
}

// Restyle mermaid's SVG to match the app, using CSS vars (not baked hex) so alpha
// survives and colors follow the mode: remap accent colors to brand ramps, fill
// node shapes — rects and the diamond/other `label-container` polygons — with
// translucent --alpha4 (rounding the rectangular ones to 8px), give edge-label
// chips a --surface-secondary background, and turn the filled-triangle arrowheads
// into open "<>" heads by dropping the closing edge and stroking them (context-
// stroke matches the line color).
function restyleSvg(svgString: string): string {
  const doc = new DOMParser().parseFromString(svgString, "image/svg+xml");
  if (doc.querySelector("parsererror")) return svgString;
  const svg = doc.documentElement;
  remapAccents(svg);
  for (const shape of svg.querySelectorAll(
    ".label-container, rect.actor, rect.note, .cluster rect"
  )) {
    shape.setAttribute(
      "style",
      `${shape.getAttribute("style") ?? ""};fill:var(--alpha4)`
    );
    if (shape.tagName.toLowerCase() === "rect") {
      shape.setAttribute("rx", "8");
      shape.setAttribute("ry", "8");
    }
  }
  for (const chip of svg.querySelectorAll("rect.background")) {
    chip.setAttribute(
      "style",
      `${chip.getAttribute("style") ?? ""};fill:var(--surface-secondary)`
    );
    chip.setAttribute("rx", "8");
    chip.setAttribute("ry", "8");
  }
  for (const marker of svg.querySelectorAll("marker")) {
    if (/cross|circle|sequencenumber/i.test(marker.id)) continue;
    const shape = marker.querySelector("path[d]");
    const d = shape?.getAttribute("d") ?? "";
    if (!shape || !/[zZ]\s*$/.test(d)) continue;
    shape.setAttribute("d", d.replace(/\s*[zZ]\s*$/, ""));
    shape.setAttribute("fill", "none");
    shape.setAttribute("stroke", "context-stroke");
    shape.setAttribute("stroke-width", "1.4");
    shape.setAttribute("stroke-linecap", "round");
    shape.setAttribute("stroke-linejoin", "round");
    shape.removeAttribute("style");
  }
  return new XMLSerializer().serializeToString(svg);
}

const Diagram = styled("div")`
  display: flex;
  justify-content: center;
  overflow-x: auto;
  padding: 24px;
  background: var(--alpha4);
  border: 0.5px solid var(--border-alpha);
  border-radius: 12px;

  svg {
    max-width: 100%;
    height: auto;
  }
`;

export function Mermaid({ source }: { source: string }) {
  const baseId = useId().replace(/[^a-zA-Z0-9-]/g, "");
  const attempt = useRef(0);
  const scheme = useColorScheme();
  const [svg, setSvg] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const renderId = `mermaid-${baseId}-${(attempt.current += 1)}`;
    (async () => {
      const mermaid = await loadMermaid();
      if (cancelled) return;
      try {
        mermaid.initialize({
          startOnLoad: false,
          securityLevel: "strict",
          theme: "base",
          htmlLabels: false,
          flowchart: { htmlLabels: false },
          themeVariables: themeVariables(scheme),
        });
        // Gate on parse first: mermaid.render() injects a "syntax error" graphic
        // into document.body on failure and doesn't clean it up, which would leak
        // onto the page for every incomplete fence while chat streams. parse()
        // with suppressErrors validates without rendering or touching the DOM.
        const parsed = await mermaid.parse(source, { suppressErrors: true });
        if (cancelled) return;
        if (!parsed) {
          setFailed(true);
          return;
        }
        const rendered = await mermaid.render(renderId, source);
        if (!cancelled) {
          setSvg(restyleSvg(rendered.svg));
          setFailed(false);
        }
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [source, scheme, baseId]);

  if (failed || svg === null)
    return (
      <pre>
        <code>{source}</code>
      </pre>
    );
  return <Diagram dangerouslySetInnerHTML={{ __html: svg }} />;
}
